import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { completeWelcome, updateName, useSession } from '../lib/session';
import Flag from '../components/Flag';

/**
 * Artboard 1c — shown once, after a first sign-in.
 * Two jobs: get a name that will read well under a photograph in the gallery,
 * and hand over the team. The team is treated as a moment rather than a form
 * field — it is the only purely delightful screen in the whole site.
 */
export default function Welcome() {
  const session = useSession();
  const navigate = useNavigate();
  const [name, setName] = useState(session?.name ?? '');
  const [saving, setSaving] = useState(false);

  if (!session) return null;

  /**
   * Save the name, then move on.
   *
   * Awaited rather than fired and forgotten, and this is the screen where that
   * matters most: before D55 this call wrote to `localStorage`, the next page
   * load overwrote it from the token, and the name a guest typed here was gone
   * — on the one screen whose entire purpose is collecting it.
   *
   * A failed write still lets them through. Standing at check-in on hotel wifi,
   * being held on a form is worse than being called by the name the identity
   * provider gave; Profile offers the same field for the rest of the week.
   */
  async function go() {
    setSaving(true);
    // `try`, because the sentence above is a promise and without this it is
    // only true by accident. `updateName` returns `{ ok: false }` rather than
    // throwing today, so nothing here would ever have caught — but if that
    // contract ever changes, an unguarded await leaves a guest stranded on this
    // screen with no way forward, standing at check-in. The cost of being sure
    // is one keyword.
    try {
      await updateName(name);
    } catch {
      // Deliberately empty: `finally` does the work, and there is nothing to
      // tell a guest standing at check-in that they could act on. The `catch`
      // is not decoration — without it the rejection escapes `go()`, the call
      // site is `void go()` and attaches no handler, and the unhandled
      // rejection fails the whole suite while every assertion in it passes.
    } finally {
      completeWelcome();
      navigate('/now', { replace: true });
    }
  }

  return (
    <div className="auth">
      <div className="auth-col">
        <h2 style={{ margin: '0 0 4px' }}>You're in, {session.name.split(' ')[0]}</h2>
        <p style={{ fontSize: 13, opacity: 0.7, margin: '0 0 18px' }}>
          Signed in as <strong>{session.email}</strong> — use that address if you come back on another phone.
        </p>

        <div className="field" style={{ marginBottom: 20 }}>
          <label htmlFor="displayName">Your name, as it will show on your photos</label>
          <input
            id="displayName" className="input" style={{ minHeight: 50, fontSize: 16 }}
            value={name} onChange={(e) => setName(e.target.value)} autoComplete="name"
          />
        </div>

        <div className="reveal">
          {/* The flag is shown, not ghosted. The suit it replaces was a 230px
              glyph bleeding off the corner at 22% opacity, which worked because
              a ♠ is legible as a silhouette. A flag is read by its colours, so
              washing it out is the one thing that stops it being recognisable —
              and this screen exists to tell somebody which flag is theirs. */}
          <div className="reveal-blob" aria-hidden="true" />
          <div style={{ position: 'relative' }}>
            <Flag team={session.team} height={72} className="reveal-flag" decorative />
            <div className="reveal-kicker">Your team</div>
            <div className="reveal-name">{session.team}</div>
            <p>Teams play the welcome dinner game on Thursday. Nothing to do until then.</p>
          </div>
        </div>

        <button type="button" className="btn btn-primary btn-block" style={{ minHeight: 54, fontSize: 16, margin: '18px 0 16px' }} onClick={() => { void go(); }} disabled={saving}>
          {saving ? 'Saving…' : 'Take me to the schedule'}
        </button>
      </div>
    </div>
  );
}
